import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { DateTime } from 'luxon';
import { immer } from 'zustand/middleware/immer';
import { produce } from 'immer';
import { API_BASE_URL } from 'apiConfig'; // 1029：取代 import.meta.env.VITE_API_URL，跟 api.js 用同一個位址
import { get, set, del } from 'idb-keyval'; // can use anything: IndexedDB, Ionic Storage, etc.

// Custom storage object
const storage = {
  getItem: async (name) => {
    return (await get(name)) || null;
  },
  setItem: async (name, value) => {
    await set(name, value);
  },
  removeItem: async (name) => {
    await del(name);
  },
};

const initialData = {
  files: {},

  // 用戶狀態
  user: {
    id: '',
    name: '',
    company: '',
    isLoggedIn: false,
  },

  // 新建專案狀態
  newProject: {
    project_id: null,
    name: '',
    quotationFile: null,
    configFile: null,
    userAudioFiles: [],      // ✅ 新增：User 多個語音檔
    engineerAudioFiles: [],  // ✅ 新增：Engineer 多個語音檔
  },

  selectedRelatedProject: {},

  // 選擇的專案狀態
  selectedProject: {
    project_id: '',
    project_name: '',
    ori_project_id: '',
    files: [],
    readOnly: false,
  },

  // 通知狀態
  notification: {
    open: false,
    message: '',
    severity: 'info', // "error", "warning", "info", "success"
  },

  // 卡片顯示狀態
  cardVisibility: {
    fileUpload: true, // 預設顯示
    createProject: false,
    databaseSelect: false,
    projectOperation: false,
    relatedProjects: false,
    materialMovement: false,
    stationList: false,
    scriptList: false,
    globalScriptFlow: false,
  },

  // 物料移動設備列表
  materialDevices: [],
  materialDeviceKeys: [],

  // 站台列表
  stations: [],
  stationKeys: [],

  // 區段流程列表
  scripts: [],
  scriptKeys: [],

  // 相關專案列表
  relatedProjects: [],

  // 全局腳本狀態
  fullScript: {
    full_script_id: '',
    all_seg_script: '',
    full_script: '',
    chat: [],
  },
};

const useAppStore = create(
  devtools(
    immer(
      persist(
        (set, get) => ({
          ...initialData,

          setter: (key, value) => {
            if (typeof value === 'function') {
              set(
                (state) => ({
                  [key]: produce(state[key], value),
                }),
                false,
              );
            } else {
              set(
                () => ({
                  [key]: value,
                }),
                false,
              );
            }
          },
          setInitEditData: (initialData) => {
            const getKeys = (baseKeys, items) => {
              if (items.length === 0) {
                return baseKeys;
              }
              const extraKeys = [];
              Object.keys(items[0]).forEach((key) => {
                if (!baseKeys.includes(key)) {
                  extraKeys.push(key);
                }
              });
              return [...baseKeys, ...extraKeys];
            };

            // 初始化zustand的編輯值
            const materialDevices = initialData.hand_transp.map(
              ({ 項目 = '', 元件及動作 = '', 類別 = '', ...others }) => ({
                id: uuidv4(),
                項目,
                元件及動作,
                類別,
                ...others,
              }),
            );

            const materialDeviceKeys = getKeys(
              ['項目', '元件及動作', '類別'],
              initialData.hand_transp,
            );

            // 轉換 station 為 stations 格式
            const stations = initialData.station.map(
              ({ 項目 = '', 元件及動作 = '', 類別 = '', ...others }) => ({
                id: uuidv4(),
                項目,
                元件及動作,
                類別,
                ...others,
              }),
            );
            const stationKeys = getKeys(
              ['項目', '元件及動作', '類別'],
              initialData.station,
            );
            const getStationValueFromName = (name) => {
              const find = stations.find(
                (station) => `${station['項目']} - ${station['類別']}` === name,
              );
              if (!find) {
                return {
                  id: 'NotFound',
                  content: name,
                };
              }
              return {
                id: find.id,
                // content: find['項目']
              };
            };
            const getDeviceValueFromName = (name) => {
              const find = materialDevices.find(
                (station) => `${station['項目']} - ${station['類別']}` === name,
              );
              if (!find) {
                return {
                  id: 'NotFound',
                  content: name,
                };
              }
              return {
                id: find.id,
                // content: find['項目']
              };
            };

            const scripts = initialData.seg_script.map(
              ({
                區段腳本名稱 = '',
                起始工站 = '',
                移動設備 = '',
                目標工站 = '',
                區段腳本編號 = '',
                ...others
              }) => {
                return {
                  id: 區段腳本編號 || uuidv4(),
                  // segScriptId: uuid,
                  區段腳本名稱,
                  起始工站: getStationValueFromName(起始工站),
                  移動設備: getDeviceValueFromName(移動設備),
                  目標工站: getStationValueFromName(目標工站),
                  ...others,
                };
              },
            );
            const scriptKeys = getKeys(
              ['區段腳本名稱', '起始工站', '移動設備', '目標工站'],
              initialData.seg_script,
            );

            set({
              materialDevices,
              stations,
              scripts,
              materialDeviceKeys,
              stationKeys,
              scriptKeys,
            });
          },

          // 全局腳本編輯的內容
          editedFullScript: '',
          setEditedFullScript: (value) => set({ editedFullScript: value }),

          // 專案操作
          projectName: '',

          // 用戶操作
          setUser: (user) => set({ user }),
          login: (id, name, company, industry) =>
            set({
              user: { id, name, company, isLoggedIn: true, industry },
            }),
          logout: () =>
            set({
              user: { id: '', name: '', company: '', isLoggedIn: false },
            }),

          // 新建專案操作
          setNewProjectName: (name) =>
            set((state) => ({ newProject: { ...state.newProject, name } })),
          setNewProjectQuotationFile: (file) =>
            set((state) => ({
              newProject: { ...state.newProject, quotationFile: file },
            })),
          // 0226新增   audioFileUser: null,   // ✅ 0226新增
          setNewProjectUserAudioFiles: (files) =>
            set((state) => {
              state.newProject.userAudioFiles = files;
            }),

          setNewProjectEngineerAudioFiles: (files) =>
            set((state) => {
              state.newProject.engineerAudioFiles = files;
            }),
          // 0226新增

          setNewProjectConfigFile: (file) =>
            set((state) => ({
              newProject: { ...state.newProject, configFile: file },
            })),
          clearNewProject: () =>
            set({
              newProject: initialData.newProject,
            }),
          createNewProject: () => {
            const { newProject } = get();
            if (!newProject.name.trim()) {
              throw new Error('請輸入專案名稱');
            }
            if (!newProject.quotationFile) {
              throw new Error('請上傳報價單檔案');
            }
            if (!newProject.configFile) {
              throw new Error('請上傳配置圖檔案');
            }

            // 這裡可以添加建立專案的 API 調用
            console.log('建立專案:', newProject);

            // 清空表單
            set({
              newProject: { name: '', quotationFile: null, configFile: null },
            });

            return { success: true, message: '專案建立成功！' };
          },

          // 專案列表操作
          setProjectList: (projectList) => set({ projectList }),
          clearProjectList: () => set({ projectList: [] }),

          // 選擇專案操作
          setSelectedProject: (project_id, project_name) =>
            set({ selectedProject: { project_id, project_name } }),
          clearSelectedProject: () =>
            set({
              selectedProject: initialData.selectedProject,
              relatedProjects: initialData.relatedProjects,
            }),

          // 獲取相關專案列表
          fetchRelatedProjects: async (project_id) => {
            try {
              const formData = new FormData();
              formData.append('user_id', localStorage.getItem('user_id') || '');
              formData.append(
                'comp_name',
                localStorage.getItem('comp_name') || '',
              );
              formData.append('project_id', project_id);

              const response = await fetch(
                `${API_BASE_URL}/getRelatedProjectList/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.project_list) {
                set({ relatedProjects: data.project_list });
                return { success: true };
              } else {
                return {
                  success: false,
                  message: data.status || '獲取相關專案失敗',
                };
              }
            } catch (error) {
              console.error('獲取相關專案錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 獲取關鍵資訊
          fetchKeyInfo: async (project_id) => {
            try {
              const formData = new FormData();
              formData.append('user_id', localStorage.getItem('user_id') || '');
              formData.append(
                'comp_name',
                localStorage.getItem('comp_name') || '',
              );
              formData.append('project_id', project_id);

              const response = await fetch(
                `${API_BASE_URL}/getKeyInfo/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.hand_transp && data.station) {
                // 轉換 hand_transp 為 materialDevices 格式
                const materialDevices = data.hand_transp.map((item, index) => ({
                  id: index + 1,
                  item: item.項目,
                  component: item.元件及動作.replace(/<br>/g, '\n'),
                  device: item.類別,
                }));

                // 轉換 station 為 stations 格式
                const stations = data.station.map((item, index) => ({
                  id: index + 1,
                  item: item.項目,
                  component: item.元件及動作.replace(/<br>/g, '\n'),
                  station: item.類別,
                }));

                set({ materialDevices, stations });

                // 當 stations 有 2 個以上且 materialDevices 有一個以上時，自動建立腳本
                if (stations.length >= 2 && materialDevices.length >= 1) {
                  const newScript = {
                    id: 1,
                    scriptName: `${stations[0].station} -> ${stations[1].station}`,
                    startStation: stations[0],
                    moveDevice: materialDevices[0],
                    targetStation: stations[1],
                    segScriptId: '', // 預設為空字串
                    chat: [], // 預設為空陣列
                  };
                  set({ scripts: [newScript] });
                }

                return { success: true };
              } else {
                return {
                  success: false,
                  message: data.status || '獲取關鍵資訊失敗',
                };
              }
            } catch (error) {
              console.error('獲取關鍵資訊錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 調整卡片顯示狀態 - 專案建立/選擇後
          adjustCardVisibilityAfterProjectSelection: () => {
            set({
              cardVisibility: {
                fileUpload: false,
                createProject: false,
                databaseSelect: false,
                projectOperation: true,
                relatedProjects: true,
                materialMovement: true,
                stationList: true,
                scriptList: true,
                globalScriptFlow: true,
              },
            });
          },

          // 通知操作
          showNotification: (message, severity = 'info') =>
            set({
              notification: {
                open: true,
                message,
                severity,
              },
            }),
          clearNotification: () =>
            set((draft) => {
              draft.notification.open = false;
            }),

          // 卡片顯示狀態操作
          setCardVisibility: (cardName, isVisible) =>
            set((state) => ({
              cardVisibility: {
                ...state.cardVisibility,
                [cardName]: isVisible,
              },
            })),
          toggleCardVisibility: (cardName) =>
            set((state) => ({
              cardVisibility: {
                ...state.cardVisibility,
                [cardName]: !state.cardVisibility[cardName],
              },
            })),
          showAllCards: () =>
            set({
              cardVisibility: {
                fileUpload: true,
                createProject: true,
                databaseSelect: true,
                projectOperation: true,
                relatedProjects: true,
                materialMovement: true,
                stationList: true,
                scriptList: true,
                globalScriptFlow: true,
              },
            }),
          hideAllCards: () =>
            set({
              cardVisibility: {
                fileUpload: false,
                createProject: false,
                databaseSelect: false,
                projectOperation: false,
                relatedProjects: false,
                materialMovement: false,
                stationList: false,
                scriptList: false,
                globalScriptFlow: false,
              },
            }),
          resetCardVisibility: () =>
            set({
              cardVisibility: {
                fileUpload: true, // 預設顯示
                createProject: false,
                databaseSelect: false,
                projectOperation: false,
                relatedProjects: false,
                materialMovement: false,
                stationList: false,
                scriptList: false,
                globalScriptFlow: false,
              },
            }),

          // 物料設備操作
          reorderMaterialDevices: (oldIndex, newIndex) => {
            return set((draft) => {
              const [movedDevice] = draft.materialDevices.splice(oldIndex, 1);
              draft.materialDevices.splice(newIndex, 0, movedDevice);
            });
          },
          reorderStations: (oldIndex, newIndex) =>
            set((draft) => {
              const [movedStation] = draft.stations.splice(oldIndex, 1);
              draft.stations.splice(newIndex, 0, movedStation);
            }),

          // 腳本操作
          addScript: (position) =>
            set((draft) => {
              const baseItem = {};
              get().scriptKeys.forEach((key) => {
                baseItem[key] = '';
              });
              const newScript = {
                id: uuidv4(),
                ...baseItem,
                chat: [], // 預設為空陣列
              };
              draft.scripts.splice(position, 0, newScript);
            }),
          deleteScript: (id) => {
            const { scripts } = get();
            set({ scripts: scripts.filter((script) => script.id !== id) });
          },
          reorderScripts: (oldIndex, newIndex) => {
            return set((draft) => {
              const [movedScript] = draft.scripts.splice(oldIndex, 1);
              draft.scripts.splice(newIndex, 0, movedScript);
            });
          },

          // 相關專案操作
          toggleRelatedProject: (id) => {
            const { relatedProjects } = get();
            const updatedProjects = relatedProjects.map((project) =>
              project.project_id === id
                ? { ...project, selected: !project.selected }
                : project,
            );
            set({ relatedProjects: updatedProjects });
          },

          // 專案名稱操作
          setProjectName: (name) => set({ projectName: name }),

          // 獲取所有狀態
          getState: () => get(),

          // 獲取區段流程列表
          fetchSegScriptList: async (script) => {
            try {
              const formData = new FormData();
              formData.append('seg_script_id', script.segScriptId);
              formData.append('flag', '0');
              formData.append('user_id', get().user.id || '');
              formData.append('comp_name', get().user.company || '');
              formData.append(
                'project_id',
                get().selectedProject.project_id || '',
              );

              const response = await fetch(
                `${API_BASE_URL}/getSegScriptList/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.seg_script_list) {
                // 更新腳本資訊，添加區段流程列表
                const { scripts } = get();
                const updatedScripts = scripts.map((s) =>
                  s.id === script.id
                    ? { ...s, segScriptList: data.seg_script_list }
                    : s,
                );
                set({ scripts: updatedScripts });

                return { success: true };
              } else {
                return {
                  success: false,
                  message: data.status || '獲取區段流程列表失敗',
                };
              }
            } catch (error) {
              console.error('獲取區段流程列表錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 刪除區段腳本版本
          deleteSegScriptVersion: async (script, version) => {
            try {
              const formData = new FormData();
              formData.append('seg_script_name', version.script_name);
              formData.append(
                'project_id',
                get().selectedProject.project_id || '',
              );
              formData.append('seg_script_id', script.segScriptId);
              formData.append('user_id', get().user.id || '');
              formData.append('comp_name', get().user.company || '');
              formData.append('edit_time', version.edit_time);

              const response = await fetch(
                `${API_BASE_URL}/deleteDBSegScript/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.status === 'success') {
                // 刪除成功後，重新獲取列表
                await get().fetchSegScriptList(script);
                return { success: true };
              } else {
                return {
                  success: false,
                  message: data.status || '刪除版本失敗',
                };
              }
            } catch (error) {
              console.error('刪除區段腳本版本錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 儲存區段腳本
          saveSegScript: async (script, segScript) => {
            try {
              const formData = new FormData();
              formData.append('seg_script_name', script.scriptName || '');
              formData.append(
                'project_id',
                get().selectedProject.project_id || '',
              );
              formData.append('seg_script_id', script.segScriptId);
              formData.append('user_id', get().user.id || '');
              formData.append('comp_name', get().user.company || '');
              formData.append(
                'edit_time',
                DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'),
              );
              formData.append('seg_script', segScript);

              const response = await fetch(
                `${API_BASE_URL}/save2DBSegScript/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.status === 'success') {
                // 儲存成功後，重新獲取列表
                await get().fetchSegScriptList(script);
                return { success: true };
              } else {
                return {
                  success: false,
                  message: data.status || '儲存腳本失敗',
                };
              }
            } catch (error) {
              console.error('儲存區段腳本錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 聊天功能
          sendChatMessage: async (segScript, question, sessionId = null) => {
            try {
              const newSessionId = sessionId || uuidv4();

              const formData = new FormData();
              formData.append('seg_script', segScript);
              formData.append('session_id', newSessionId);
              formData.append('question', question);

              const response = await fetch(
                `${API_BASE_URL}/segScriptChat/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.reply) {
                return {
                  success: true,
                  reply: data.reply,
                  sessionId: data.session_id,
                  replySegScript: data.reply_seg_script,
                };
              } else {
                return {
                  success: false,
                  message: data.status || '聊天請求失敗',
                };
              }
            } catch (error) {
              console.error('聊天請求錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 全局腳本操作
          setFullScript: (fullScript) => set({ fullScript }),
          updateFullScript: (field, value) => {
            const { fullScript } = get();
            set({ fullScript: { ...fullScript, [field]: value } });
          },
          clearFullScript: () =>
            set({
              fullScript: {
                full_script_id: '',
                all_seg_script: '',
                full_script: '',
                chat: [],
              },
            }),

          // 生成全局腳本
          generateFullScript: async () => {
            try {
              const { scripts, fullScript } = get();

              // 檢查所有腳本是否都有 segScriptId
              const scriptsWithSegScriptId = scripts.filter(
                (script) => script.segScriptId,
              );
              if (scriptsWithSegScriptId.length === 0) {
                return { success: false, message: '沒有可用的區段腳本' };
              }

              // 生成 all_seg_script JSON
              const allSegScriptObj = {};
              scriptsWithSegScriptId.forEach((script) => {
                allSegScriptObj[script.segScriptId] = script.segScript || '';
              });
              const newAllSegScript = JSON.stringify(allSegScriptObj);

              // 檢查是否需要重新生成
              const needsRegeneration =
                fullScript.all_seg_script !== newAllSegScript;

              if (needsRegeneration || !fullScript.full_script_id) {
                // 生成新的 full_script_id
                const newFullScriptId = uuidv4();

                const formData = new FormData();
                formData.append('full_script_id', newFullScriptId);
                formData.append(
                  'project_id',
                  get().selectedProject.project_id || '',
                );
                formData.append('all_seg_script', newAllSegScript);

                const response = await fetch(
                  `${API_BASE_URL}/genFullScript/`,
                  {
                    method: 'POST',
                    body: formData,
                  },
                );

                const data = await response.json();

                if (response.ok && data.full_script) {
                  // 更新全局腳本狀態
                  set({
                    fullScript: {
                      full_script_id: newFullScriptId,
                      all_seg_script: newAllSegScript,
                      full_script: data.full_script,
                      chat: fullScript.chat || [],
                    },
                  });
                  return { success: true, message: '全局腳本生成成功！' };
                } else {
                  return {
                    success: false,
                    message: data.status || '全局腳本生成失敗',
                  };
                }
              } else {
                // 不需要重新生成，直接返回成功
                return { success: true, message: '全局腳本已是最新版本' };
              }
            } catch (error) {
              console.error('生成全局腳本錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 全局腳本聊天功能
          sendFullScriptChatMessage: async (
            fullScript,
            question,
            sessionId = null,
          ) => {
            try {
              const newSessionId = sessionId || uuidv4();

              const formData = new FormData();
              formData.append('full_script', fullScript);
              formData.append('session_id', newSessionId);
              formData.append('question', question);

              const response = await fetch(
                `${API_BASE_URL}/fullScriptChat/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.reply) {
                return {
                  success: true,
                  reply: data.reply,
                  sessionId: data.session_id,
                  replyFullScript: data.reply_full_script,
                };
              } else {
                return {
                  success: false,
                  message: data.status || '聊天請求失敗',
                };
              }
            } catch (error) {
              console.error('全局腳本聊天請求錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 獲取全局腳本列表
          fetchFullScriptList: async (fullScript) => {
            try {
              const formData = new FormData();
              formData.append('full_script_id', fullScript.full_script_id);
              formData.append('flag', '0');
              formData.append('user_id', get().user.id || '');
              formData.append('comp_name', get().user.company || '');
              formData.append(
                'project_id',
                get().selectedProject.project_id || '',
              );

              const response = await fetch(
                `${API_BASE_URL}/getFullScriptList/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.full_script_list) {
                // 更新全局腳本資訊，添加全局腳本列表
                const { fullScript: currentFullScript } = get();
                const updatedFullScript = {
                  ...currentFullScript,
                  fullScriptList: data.full_script_list,
                };
                set({ fullScript: updatedFullScript });

                return { success: true };
              } else {
                return {
                  success: false,
                  message: data.status || '獲取全局腳本列表失敗',
                };
              }
            } catch (error) {
              console.error('獲取全局腳本列表錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 刪除全局腳本版本
          deleteFullScriptVersion: async (fullScript, version) => {
            try {
              const formData = new FormData();
              formData.append('full_script_name', version.script_name);
              formData.append(
                'project_id',
                get().selectedProject.project_id || '',
              );
              formData.append('full_script_id', fullScript.full_script_id);
              formData.append('user_id', get().user.id || '');
              formData.append('comp_name', get().user.company || '');
              formData.append('edit_time', version.edit_time);

              const response = await fetch(
                `${API_BASE_URL}/deleteDBFullScript/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.status === 'success') {
                // 刪除成功後，重新獲取列表
                await get().fetchFullScriptList(fullScript);
                return { success: true };
              } else {
                return {
                  success: false,
                  message: data.status || '刪除版本失敗',
                };
              }
            } catch (error) {
              console.error('刪除全局腳本版本錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },

          // 儲存全局腳本
          saveFullScript: async (fullScript, fullScriptContent) => {
            try {
              const formData = new FormData();
              formData.append('full_script_id', fullScript.full_script_id);
              formData.append(
                'project_id',
                get().selectedProject.project_id || '',
              );
              formData.append('user_id', get().user.id || '');
              formData.append('comp_name', get().user.company || '');
              formData.append(
                'edit_time',
                DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'),
              );
              formData.append('full_script', fullScriptContent);

              const response = await fetch(
                `${API_BASE_URL}/save2DBFullScript/`,
                {
                  method: 'POST',
                  body: formData,
                },
              );

              const data = await response.json();

              if (response.ok && data.status === 'success') {
                // 儲存成功後，重新獲取列表
                await get().fetchFullScriptList(fullScript);
                return { success: true };
              } else {
                return {
                  success: false,
                  message: data.status || '儲存全局腳本失敗',
                };
              }
            } catch (error) {
              console.error('儲存全局腳本錯誤:', error);
              return { success: false, message: '網路錯誤，請稍後再試' };
            }
          },
        }),
        {
          name: 'plc-storage',
          partialize: (state) => ({
            user: state.user,
          }),
          storage: createJSONStorage(() => storage),
        },
      ),
    ),
    {
      name: 'plc',
    },
  ),
);

export default useAppStore;
